import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * EliosErrorBoundary — protezione anti-crash SPA per le pagine Elios.
 *
 * Quando un errore JavaScript risale oltre un componente interno (es. un
 * oggetto passato a toast.error, un render che si aspetta un array e riceve
 * `undefined`), React smonta tutto l'albero figlio dell'ErrorBoundary e
 * renderizza un fallback "amichevole" invece di lasciare la pagina nera.
 *
 * Uso:
 *   <EliosErrorBoundary>
 *     <SomePage />
 *   </EliosErrorBoundary>
 *
 * In dev (NODE_ENV !== 'production') mostra anche stack + errore per debug.
 */
class EliosErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Se avessimo Sentry/LogRocket in futuro, è qui che andrebbe l'invio.
    // Per ora, almeno stampiamo in console (si vede nei log browser e nei
    // test Playwright).
    try {
      // eslint-disable-next-line no-console
      console.error('[EliosErrorBoundary]', error, info?.componentStack);
    } catch {}
    this.setState({ info });
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
    const pageName = this.props.pageName || 'pagina';
    const errMsg = this.state.error?.message || String(this.state.error || 'Errore sconosciuto');

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="elios-error-boundary">
        <div className="max-w-lg w-full bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                La {pageName} ha incontrato un errore
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Non preoccuparti: il resto della piattaforma continua a funzionare.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-mono text-red-700 break-all">{errMsg}</p>
            {isDev && this.state.info?.componentStack && (
              <details className="mt-2">
                <summary className="text-[10px] text-gray-500 cursor-pointer">Stack trace (solo dev)</summary>
                <pre className="text-[10px] text-gray-500 mt-1 whitespace-pre-wrap break-all">{this.state.info.componentStack}</pre>
              </details>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={this.reset}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
              data-testid="elios-error-boundary-retry"
            >
              <RefreshCw className="w-4 h-4" /> Riprova
            </button>
            <a
              href="/elios/dashboard"
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium transition-colors"
              data-testid="elios-error-boundary-home"
            >
              <Home className="w-4 h-4" /> Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default EliosErrorBoundary;
